import React from 'react';
import { useTranslation } from 'react-i18next';
import { DatePicker as CustomDatePicker } from '../../../../components/dateTimePickers';
import { CompanyVariablePayTypeView, UserVariablePayInput } from '../../../../models/payroll';
import { Dialog, DialogContent as RadixDialogContent, DialogHeader, DialogBody, DialogFooter, DialogTitle } from '../../../../components/ui/Dialog';
import Input from '../../../../components/ui/Input';
import Select from '../../../../components/ui/Select';
import Button from '../../../../components/ui/Button';
import AppIcon from '../../../../components/AppIcon';

// Shared header pattern
const DialogHeaderIcon: React.FC<{ icon: React.ReactNode; title: string; }> = ({ icon, title }) => (
  <DialogHeader>
    <div className="flex items-center gap-2">
      {icon}
      <DialogTitle>{title}</DialogTitle>
    </div>
  </DialogHeader>
);

type BaseProps = { loading: boolean };

// Add Variable Pay Dialog
export interface AddVariablePayDialogProps extends BaseProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  input: UserVariablePayInput;
  setInput: (v: UserVariablePayInput) => void;
  variablePayTypes: CompanyVariablePayTypeView[];
  errorVisible: boolean;
}

export const AddVariablePayDialog: React.FC<AddVariablePayDialogProps> = ({
  open,
  onClose,
  onSubmit,
  input,
  setInput,
  variablePayTypes,
  errorVisible,
  loading,
}) => {
  const { t } = useTranslation('common');
  const payItemOptions = variablePayTypes.map(x => ({ label: x.name, value: x.id }));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <RadixDialogContent size="sm">
        <DialogHeaderIcon icon={<AppIcon name="Plus" size={16} className="text-primary" />} title={t('ADD VARIABLE PAY')} />
        <DialogBody>
          <div className="flex flex-col gap-4">
            {errorVisible && (
              <p className="text-sm text-destructive" role="alert">
                {t('Please fill all required fields')}
              </p>
            )}
            <Select
              label={t('Pay Item')}
              options={payItemOptions}
              value={input.companyVariablePayTypeId}
              onChange={(value: any) => value && setInput({ ...input, companyVariablePayTypeId: value })}
              searchable
            />
            <Input
              label={t('Amount')}
              type="number"
              value={input.amount ?? ''}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, amount: parseFloat(ev.target.value) })}
            />
            <Input
              label={t('Remarks')}
              value={input.remarks ?? ''}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, remarks: ev.target.value })}
            />
            <CustomDatePicker
              label={t('Effective Date')}
              format="DD/MM/YYYY"
              value={input.effectiveDate ? input.effectiveDate : null}
              onChange={(ev: any) => setInput({ ...input, effectiveDate: ev ?? undefined })}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="default" onClick={onSubmit} disabled={loading} loading={loading}>
            {t('Submit')}
          </Button>
        </DialogFooter>
      </RadixDialogContent>
    </Dialog>
  );
};

// Edit Variable Pay Dialog
export interface EditVariablePayDialogProps extends BaseProps {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  input: UserVariablePayInput;
  setInput: (v: UserVariablePayInput) => void;
  variablePayTypes: CompanyVariablePayTypeView[];
}

export const EditVariablePayDialog: React.FC<EditVariablePayDialogProps> = ({
  open,
  onClose,
  onSubmit,
  input,
  setInput,
  variablePayTypes,
  loading,
}) => {
  const { t } = useTranslation('common');
  const currentType = variablePayTypes.find(x => x.id === input.companyVariablePayTypeId);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <RadixDialogContent size="sm">
        <DialogHeaderIcon icon={<AppIcon name="Pencil" size={16} className="text-primary" />} title={t('EDIT VARIABLE PAY')} />
        <DialogBody>
          <div className="flex flex-col gap-4">
            <Input
              label={t('Pay Item')}
              value={currentType?.name || ''}
              disabled
            />
            <Input
              label={t('Amount')}
              type="number"
              value={input.amount ?? ''}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, amount: parseFloat(ev.target.value) })}
            />
            <Input
              label={t('Remarks')}
              value={input.remarks ?? ''}
              onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setInput({ ...input, remarks: ev.target.value })}
            />
            <CustomDatePicker
              label={t('Effective Date')}
              format="DD/MM/YYYY"
              value={input.effectiveDate ? input.effectiveDate : null}
              onChange={(ev: any) => setInput({ ...input, effectiveDate: ev ?? undefined })}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="default" onClick={onSubmit} disabled={loading} loading={loading}>
            {t('Submit')}
          </Button>
        </DialogFooter>
      </RadixDialogContent>
    </Dialog>
  );
};

// Delete Confirmation Dialog
export interface DeleteVariablePayDialogProps extends BaseProps {
  open: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export const DeleteVariablePayDialog: React.FC<DeleteVariablePayDialogProps> = ({ open, onCancel, onConfirm, loading }) => {
  const { t } = useTranslation('common');
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <RadixDialogContent size="sm">
        <DialogHeaderIcon icon={<AppIcon name="Trash2" size={16} className="text-primary" />} title={t('Delete Variable Pay')} />
        <DialogBody>
          <p className="text-sm text-muted-foreground">
            {t('Are you sure you want to delete this record?')}
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={loading}>
            {t('Cancel')}
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={loading} loading={loading}>
            {t('Delete')}
          </Button>
        </DialogFooter>
      </RadixDialogContent>
    </Dialog>
  );
};

export default {
  AddVariablePayDialog,
  EditVariablePayDialog,
  DeleteVariablePayDialog
};
